import React from 'react';
import { Card, Form, Select, Button, Space, message, Divider } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { useSettings } from '../contexts/SettingsContext';
import { SUPPORTED_LANGUAGES, SUPPORTED_TIMEZONES } from '../types/settings';
import { useI18n } from '../hooks/useI18n';
import TimeDisplay from './TimeDisplay';

const { Option } = Select;

interface UserSettingsProps {
  embedded?: boolean;
}

const UserSettings: React.FC<UserSettingsProps> = ({ embedded = false }) => {
  const { settings, updateSettings, resetSettings, isLoading } = useSettings();
  const { t } = useI18n();
  const [form] = Form.useForm();

  // 处理表单提交
  const handleSubmit = async (values: any) => {
    try {
      await updateSettings(values);
    } catch (error) {
      // 错误已在context中处理
    }
  };

  // 处理重置设置
  const handleReset = async () => {
    try {
      await resetSettings();
      form.resetFields();
    } catch (error) {
      // 错误已在context中处理
    }
  };

  const Inner = (
      <>
      <Form
        form={form}
        layout="vertical"
        initialValues={settings}
        onFinish={handleSubmit}
        autoComplete="off"
      >
        {/* 语言设置 */}
        <Form.Item
          label={t('settings.language')}
          name="language"
          rules={[{ required: true, message: '请选择语言' }]}
        >
          <Select placeholder={t('settings.languagePlaceholder')}>
            {SUPPORTED_LANGUAGES.map(lang => (
              <Option key={lang.code} value={lang.code}>
                <Space>
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                  <span style={{ color: '#999', fontSize: '12px' }}>({lang.code})</span>
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* 时区设置 */}
        <Form.Item
          label={t('settings.timezone')}
          name="timezone"
          rules={[{ required: true, message: '请选择时区' }]}
        >
          <Select placeholder={t('settings.timezonePlaceholder')}>
            {SUPPORTED_TIMEZONES.map(timezone => (
              <Option key={timezone.code} value={timezone.code}>
                <Space>
                  <span>{timezone.name}</span>
                  <span style={{ color: '#999', fontSize: '12px' }}>{timezone.offset}</span>
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* 主题设置 */}
        <Form.Item
          label={t('settings.theme')}
          name="theme"
          rules={[{ required: true, message: '请选择主题' }]}
        >
          <Select placeholder={t('settings.themePlaceholder')}>
            <Option value="light">浅色主题</Option>
            <Option value="dark">深色主题</Option>
            <Option value="auto">跟随系统</Option>
          </Select>
        </Form.Item>

        <Divider />

        {/* 操作按钮 */}
        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={isLoading}
            >
              {t('common.save')}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              loading={isLoading}
            >
              {t('settings.resetToDefault')}
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* 设置说明 */}
      <Divider orientation="left">{t('settings.instructions')}</Divider>
      <div style={{ color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
        <p>• <strong>{t('settings.language')}：</strong>{t('settings.languageDesc')}</p>
        <p>• <strong>{t('settings.timezone')}：</strong>{t('settings.timezoneDesc')}</p>
        <p>• <strong>{t('settings.theme')}：</strong>{t('settings.themeDesc')}</p>
        <p>• <strong>{t('settings.dataStorage')}：</strong>{t('settings.dataStorageDesc')}</p>
        <p>• <strong>{t('settings.settingsSync')}：</strong>{t('settings.settingsSyncDesc')}</p>
      </div>
      </>
  );

  if (embedded) {
    return Inner;
  }

  return (
    <Card title={t('settings.title')} style={{ maxWidth: 800, margin: '20px auto' }}>
      {Inner}
    </Card>
  );
};

export default UserSettings;
